import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, ChevronRight, FileText } from 'lucide-react';
import mapi from './api';
import { MobileShell, MobileHeader, MobileSheet, C } from './MobileShell';
import { useMobile } from './MobileContext';
import DateInput from '@/components/ui/date-input';

const FILTER = [
  { k: 'alle', label: 'Alle' },
  { k: 'schueler', label: 'Meine Schüler:innen' },
  { k: 'kurse', label: 'Meine Kurse' },
];

function zeit(eintrag) {
  const uhr = [eintrag.start, eintrag.end].filter(Boolean).join('–');
  return [eintrag.weekday, uhr].filter(Boolean).join(', ');
}

function datum(iso) {
  const text = String(iso || '').slice(0, 10);
  const [jahr, monat, tag] = text.split('-');
  if (!tag) return text;
  return `${tag}.${monat}.${jahr}`;
}

function leerFormular() {
  return { lesson_on: '', content: '', homework: '', progress: '', links: [] };
}

export default function MobileLogbook() {
  const { courseId } = useParams();
  return courseId ? <LogbuchEintraege courseId={courseId} /> : <LogbuchListe />;
}

function LogbuchListe() {
  const nav = useNavigate();
  const { teacherId, area } = useMobile();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('alle');
  const [query, setQuery] = useState('');

  const load = () => {
    const params = { ...(teacherId ? { teacher_id: teacherId } : {}), ...(area ? { area } : {}) };
    mapi.get('/app/logbook', { params })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  };
  useEffect(load, [teacherId, area]);

  const needle = query.trim().toLowerCase();
  const shown = items.filter((eintrag) => {
    const gruppe = eintrag.kind === 'kurs' ? 'kurse' : eintrag.kind;
    if (filter !== 'alle' && gruppe !== filter) return false;
    if (!needle) return true;
    return (eintrag.title || '').toLowerCase().includes(needle);
  });

  return (
    <MobileShell onAction={() => nav('/m/files')} actionIcon={ArrowLeft} actionLabel="Zurück">
      <MobileHeader title="ShareSpace" backTo="/m/files" onRefresh={load} />
      <div className="flex-1 overflow-y-auto px-4 py-3 pb-10">
        <div className="mb-3 flex items-start gap-3 px-1">
          <BookOpen className="mt-0.5 h-6 w-6 shrink-0" style={{ color: C.red }} />
          <div>
            <h2 className="text-lg font-bold" style={{ color: C.text }} data-testid="mobile-logbuch-title">
              Logbuch
            </h2>
            <p className="text-xs" style={{ color: C.dim }}>
              Notizen, Aufgaben und Unterrichtsfortschritte pro Schüler:in oder Kurs.
            </p>
          </div>
        </div>
        <div className="mb-3 flex items-center rounded-lg px-3 py-2"
             style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Schüler:in / Kurs suchen…"
            data-testid="mobile-logbuch-search"
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: C.text }}
          />
        </div>
        <div className="mb-3 flex gap-2 overflow-x-auto">
          {FILTER.map(({ k, label }) => {
            const on = filter === k;
            return (
              <button
                key={k}
                type="button"
                data-testid={`mobile-logbuch-filter-${k}`}
                className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold"
                style={{
                  background: on ? C.red : C.card,
                  color: on ? '#fff' : C.text,
                  border: `1px solid ${on ? C.red : C.border}`,
                }}
                onClick={() => setFilter(k)}
              >
                {label}
              </button>
            );
          })}
        </div>
        {!shown.length && (
          <p className="mt-10 text-center text-sm" style={{ color: C.dim }} data-testid="mobile-logbuch-empty">
            Kein Unterricht zugeordnet.
          </p>
        )}
        {shown.map((eintrag) => (
          <button
            key={eintrag.id}
            type="button"
            data-testid={`mobile-logbuch-${eintrag.id}`}
            className="mb-3 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left"
            style={{ background: C.card, border: `1px solid ${C.border}` }}
            onClick={() => nav(`/m/logbuch/${eintrag.id}`)}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: C.rose }}>
              <BookOpen className="h-5 w-5" style={{ color: C.red }} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold" style={{ color: C.text }}>
                {eintrag.title}
              </span>
              <span className="block text-[11px]" style={{ color: C.dim }}>
                {eintrag.form}
                {eintrag.participants != null ? ` · ${eintrag.participants} Teilnehmende` : ''}
              </span>
              {zeit(eintrag) && (
                <span className="block text-[11px]" style={{ color: C.dim }}>{zeit(eintrag)}</span>
              )}
            </span>
            <span className="shrink-0 text-[11px]" style={{ color: C.dim }}>
              {eintrag.entries} Einträge
            </span>
            <ChevronRight className="h-4 w-4 shrink-0" style={{ color: C.dim }} />
          </button>
        ))}
      </div>
    </MobileShell>
  );
}

function LogbuchEintraege({ courseId }) {
  const nav = useNavigate();
  const { teacherId, area } = useMobile();
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    const params = { ...(teacherId ? { teacher_id: teacherId } : {}), ...(area ? { area } : {}) };
    mapi.get(`/app/logbook/${courseId}`, { params })
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null));
  };
  useEffect(load, [courseId, teacherId, area]);
  useEffect(() => {
    if (!detail?.can_write) return undefined;
    mapi.get('/app/documents')
      .then((r) => setFiles((r.data || []).filter((row) => row.kind !== 'folder')))
      .catch(() => setFiles([]));
    return undefined;
  }, [detail?.can_write]);

  const save = async () => {
    if (!form?.lesson_on) {
      toast.error('Bitte ein Datum angeben');
      return;
    }
    setSaving(true);
    try {
      const body = {
        lesson_on: form.lesson_on,
        content: form.content || '',
        homework: form.homework || '',
        progress: form.progress || '',
        links: form.links || [],
      };
      if (form.id) await mapi.put(`/app/logbook/entries/${form.id}`, body);
      else await mapi.post(`/app/logbook/${courseId}/entries`, body);
      setForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Eintrag konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const item = detail?.item;
  const entries = detail?.entries || [];

  return (
    <MobileShell onAction={() => nav('/m/files')} actionIcon={ArrowLeft} actionLabel="Zurück">
      <MobileHeader title="Logbuch" backTo="/m/logbuch" onRefresh={load} />
      <div className="flex-1 overflow-y-auto px-4 py-3 pb-10" data-testid="mobile-logbuch-detail">
        {!item && (
          <p className="mt-10 text-center text-sm" style={{ color: C.dim }}>Logbuch nicht verfügbar.</p>
        )}
        {item && (
          <>
            <h2 className="text-lg font-bold" style={{ color: C.text }} data-testid="mobile-logbuch-detail-title">
              {item.title}
            </h2>
            <p className="mb-4 text-xs" style={{ color: C.dim }}>
              {[item.form, zeit(item)].filter(Boolean).join(' · ')}
            </p>
            {detail.can_write && (
              <button
                type="button"
                data-testid="mobile-logbuch-new"
                className="mb-4 w-full rounded-lg py-3 text-sm font-semibold text-white"
                style={{ background: C.red }}
                onClick={() => setForm(leerFormular())}
              >
                Eintrag
              </button>
            )}
            {!entries.length && (
              <p className="text-center text-sm" style={{ color: C.dim }} data-testid="mobile-logbuch-detail-empty">
                Noch keine Einträge.
              </p>
            )}
            {entries.map((eintrag) => (
              <article
                key={eintrag.id}
                data-testid={`mobile-logbuch-entry-${eintrag.id}`}
                className="mb-3 rounded-xl px-3 py-3"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold" style={{ color: C.text }}>{datum(eintrag.lesson_on)}</p>
                  {detail.can_write && (
                    <button
                      type="button"
                      className="text-xs font-semibold"
                      style={{ color: C.red }}
                      data-testid={`mobile-logbuch-edit-${eintrag.id}`}
                      onClick={() => setForm({ ...eintrag, links: eintrag.links || [] })}
                    >
                      Bearbeiten
                    </button>
                  )}
                </div>
                {eintrag.content && <Feld label="Unterricht" text={eintrag.content} />}
                {eintrag.homework && <Feld label="Hausaufgaben" text={eintrag.homework} />}
                {eintrag.progress && <Feld label="Lernfortschritt" text={eintrag.progress} />}
                {(eintrag.links || []).map((link) => (
                  <button
                    key={link.document_id}
                    type="button"
                    className="mt-2 flex items-center gap-2 text-xs font-semibold"
                    style={{ color: C.red }}
                    onClick={() => nav(`/m/files?datei=${link.document_id}`)}
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {link.title || 'Datei'}
                  </button>
                ))}
              </article>
            ))}
          </>
        )}
      </div>
      {form && (
        <MobileSheet scrim="rgba(0,0,0,0.45)" onClick={() => setForm(null)}>
          <div className="w-full max-w-md space-y-3 rounded-t-2xl p-4" style={{ background: C.card }}
               onClick={(event) => event.stopPropagation()} data-testid="mobile-logbuch-form">
            <p className="text-sm font-bold">{form.id ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</p>
            <DateInput value={form.lesson_on} onChange={(e) => setForm({ ...form, lesson_on: e.target.value })} />
            <FeldInput label="Unterrichtsinhalt" value={form.content} testId="mobile-logbuch-content"
                       onChange={(value) => setForm({ ...form, content: value })} />
            <FeldInput label="Hausaufgaben" value={form.homework} testId="mobile-logbuch-homework"
                       onChange={(value) => setForm({ ...form, homework: value })} />
            <FeldInput label="Lernfortschritt" value={form.progress} testId="mobile-logbuch-progress"
                       onChange={(value) => setForm({ ...form, progress: value })} />
            {!!files.length && (
              <div className="flex flex-wrap gap-2">
                {files.map((file) => {
                  const on = (form.links || []).some((link) => link.document_id === file.id);
                  return (
                    <button
                      key={file.id}
                      type="button"
                      className="rounded-full px-3 py-1 text-xs font-semibold"
                      style={{ background: on ? C.rose : C.bg, color: C.text, border: `1px solid ${C.border}` }}
                      onClick={() => {
                        const links = on
                          ? form.links.filter((link) => link.document_id !== file.id)
                          : [...(form.links || []), { document_id: file.id, title: file.title }];
                        setForm({ ...form, links });
                      }}
                    >
                      {file.title}
                    </button>
                  );
                })}
              </div>
            )}
            <button type="button" disabled={saving} data-testid="mobile-logbuch-save"
                    className="w-full rounded-lg py-3 text-sm font-semibold text-white disabled:opacity-50"
                    style={{ background: C.red }} onClick={save}>
              Speichern
            </button>
          </div>
        </MobileSheet>
      )}
    </MobileShell>
  );
}

function Feld({ label, text }) {
  return (
    <p className="mt-1 text-xs" style={{ color: C.text }}>
      <span className="font-semibold" style={{ color: C.dim }}>{label}: </span>
      {text}
    </p>
  );
}

function FeldInput({ label, value, onChange, testId }) {
  return (
    <label className="block text-xs font-semibold" style={{ color: C.dim }}>
      {label}
      <textarea
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
        rows={2}
        className="mt-1 w-full rounded-lg px-3 py-2 text-sm font-normal outline-none"
        style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
      />
    </label>
  );
}
